// Транспорт и хуки раздела «Деньги»: касса регионов и ФОТ (ядро → money_api.py).
//
// ОТДЕЛЬНЫЙ ФАЙЛ, А НЕ ДОПИСКА В api/pex.ts — И ЭТО НЕ ВКУСОВЩИНА.
// В шапке api/pex.ts прямо сказано: кассы и ФОТ в том контракте нет вовсе, потому что
// ядро их не читает, и «чтобы добавить кассу на экран, придётся сначала открыть её
// в ядре — то есть принять решение осознанно, а не дописать поле». Решение принято, касса
// открыта — но открыта ОДНОЙ учётке владельца. Если положить её типы рядом с отчётами
// руководителя, следующий человек допишет туда же соседний хук и раздаст зарплаты
// половине компании, не заметив. Здесь у файла одна тема и одна оговорка в шапке.
//
// ЧТО ЗДЕСЬ НЕ ЗАЩИТА. Ничего. Ограничение доступа стоит на сервере в двух местах: роль
// в BFF и проверка владельца в самом модуле ядра. Браузер лишь не рисует то, чего ему
// всё равно не отдадут: если этот файл вызвать чужой учёткой, приедет 403.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/money'

/** Раздел закрыт этой учётке. Отдельный тип: 403 здесь — не поломка, а нормальный ответ. */
export class НеВладелец extends Error {
  constructor() {
    super('Касса регионов и ФОТ видны только владельцу')
    this.name = 'НеВладелец'
  }
}

/** Тот же разбор ответа, что в api/client.ts и api/pex.ts, плюс 403 отдельным типом. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  if (res.status === 403) throw new НеВладелец()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 от ядра — «такой фильтр обслужить нельзя». Пустая таблица здесь читалась бы
    // как «выплат не было», а это про зарплаты — поэтому поднимаем тот же тип, что и
    // остальные разделы, и QueryState печатает объяснение вместо пустого списка.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────── общие части ответов (см. money_api.py) ───────────

/**
 * Сколько операций отсечено как мусор разбора и по какому правилу.
 *
 * Приезжает с КАЖДЫМ ответом, в том числе когда отсекать было нечего. Экран обязан это
 * показывать: «0 отсечено» и «мы не проверяли» — разные утверждения, а без отсечения
 * касса Владивостока показывает миллиарды.
 */
export interface Отсечение {
  /** Сколько операций отсечено — по тем же фильтрам, что и сам отчёт. */
  ops: number
  spent: number
  received: number
  /** Из чего сложилось. Признаки пересекаются, поэтому их сумма бывает больше `ops`. */
  by_category: number
  by_amount: number
  /** Самая большая отсечённая сумма — по ней видно масштаб дефекта. */
  worst: number
  /** Действующий порог суммы. */
  cap: number
  /** Правило словами — показываем как есть, своими словами не пересказываем. */
  rule: string
}

/** Оговорки о данных. Ядро присылает их каждым ответом, и экран обязан их показать. */
export type Оговорки = Record<string, string>

export interface Ответ {
  cut: Отсечение
  data_notes: Оговорки
}

export interface Страница<T> extends Ответ {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
}

// ─────────── справочники (/money/refs) ───────────

export interface РегионСправочник {
  region: string
  /** Бизнес-юнит сделок. Единственный мостик «касса → юнит»; бывает пуст. */
  dept: string | null
  sort: number | null
}

export interface СрезСчёт {
  k: string
  n: number
  spent?: number
}

export interface Справочники extends Ответ {
  regions: РегионСправочник[]
  regions_in_data: СрезСчёт[]
  /**
   * Регион есть в кассе, но его нет в справочнике «регион → бизнес-юнит». Ровно на таком
   * расхождении имён четыре руководителя из восьми видели нули в exec-панели, поэтому
   * список приезжает отдельным полем, а не сводится молча.
   */
  regions_unmapped: string[]
  categories: СрезСчёт[]
  cards: СрезСчёт[]
  kinds: СрезСчёт[]
  months: string[]
  counts: {
    ops_all: number
    ops_junk: number
    payout_ops: number
    payout_spent: number
    payout_people: number
    balances: number
    regions_ref: number
    /** Строк в pex_fot. Сегодня ноль — и это факт, который надо назвать словами. */
    fot_rows: number
    cash_expense_rows: number
  }
  synced: { ops: string | null; first_day: string | null; last_day: string | null }
  payout_categories: string[]
  cap_default: number
  cap_min: number
  cap_max: number
}

// ─────────── касса и ФОТ: агрегаты (/money/cash, /money/fot) ───────────

/** Строка агрегата. Колонки разреза приходят под своими именами (region, ym, …). */
export interface СтрокаАгрегата {
  region?: string
  ym?: string
  category?: string
  card?: string
  kind?: string
  direction?: string
  ops: number
  spent: number
  received: number
  first_day: string | null
  last_day: string | null
  /** Операций с заполненным получателем — по нему видно, насколько полон разрез по людям. */
  named: number
}

export interface Агрегат extends Страница<СтрокаАгрегата> {
  /** Какие разрезы применены: ['region'] или ['region','ym']. */
  by: string[]
  /** Итог по ВСЕЙ выборке, а не по странице. */
  total_row: {
    ops: number
    spent: number
    received: number
    regions?: number
    ym_first?: string | null
    ym_last?: string | null
  }
  payouts_only: boolean
  payout_categories: string[]
}

// ─────────── ФОТ по людям (/money/fot-people) ───────────

export interface СтрокаЧеловека {
  /** ФИО получателя как оно записано в кассе, либо «(получатель не указан)». */
  person: string
  /** false — получатель не указан: фамилия осталась внутри комментария. */
  named: boolean
  ops: number
  spent: number
  ym_first: string | null
  ym_last: string | null
  first_day: string | null
  last_day: string | null
  regions: string[]
  categories: string[]
}

export interface ФОТПоЛюдям extends Страница<СтрокаЧеловека> {
  total_row: {
    ops: number
    spent: number
    /** Разных получателей с заполненным ФИО. */
    people: number
    /** Операции без указанного получателя — это НЕ ноль людей, а нераспознанные. */
    unnamed_ops: number
    unnamed_spent: number
  }
  payout_categories: string[]
}

// ─────────── остатки (/money/balances) ───────────

export interface СтрокаОстатка {
  bal_key: string
  region: string | null
  kind: string | null
  ym: string | null
  opening: number | null
  /** В источнике заполнен не всегда. null — сверять не с чем. */
  closing: number | null
  src_rows: number | null
  sheet: string | null
  ops: number
  spent: number
  received: number
  /** Остаток, посчитанный по операциям: начало + приход − расход (мусор отсечён). */
  calc_closing: number
  /** Расхождение источника с расчётом. null — остатка на конец в источнике нет. */
  diff: number | null
}

export type Остатки = Страница<СтрокаОстатка>

// ─────────── движение (/money/ops) ───────────

export interface Операция {
  src_id: number
  region: string | null
  kind: string | null
  ym: string | null
  op_date: string | null
  /** Исходная строка даты. Заполнена там, где дату не разобрать («202-09-06»). */
  op_date_raw: string | null
  fio: string | null
  direction: string | null
  category: string | null
  deal_raw: string | null
  spent: number | null
  received: number | null
  comment: string | null
  card: string | null
  src: string | null
  created_by: string | null
  created_at: string | null
}

export interface Движение extends Страница<Операция> {
  total_row: { ops: number; spent: number; received: number }
  /** true — показаны ровно те строки, которые отчёт отсекает (?junk=1). */
  junk_only: boolean
  payout_categories: string[]
}

// ─────────── хуки ───────────
// staleTime большой: зеркало обновляется синком, а касса приезжает из xlsx раз в месяц —
// перезапрашивать её на каждый фокус окна незачем.
//
// retry: 0, а не 1. Отказ по правам повторять бессмысленно, а каждая попытка — ещё одна
// строка в журнале доступа к зарплатам. Журнал должен показывать, сколько раз человек
// СМОТРЕЛ, а не сколько раз react-query дёрнул сеть.
const общее = { retry: 0, staleTime: 5 * 60_000 } as const

export const useДеньгиСправочники = () =>
  useQuery({ queryKey: ['money', 'refs'], queryFn: () => запрос<Справочники>('/refs'), ...общее })

// `enabled` у агрегатов не для экономии сети. Каждый запрос сюда — строка в журнале
// доступа к зарплатам, и журнал должен отвечать «человек СМОТРЕЛ кассу», а не «экран
// открыт, поэтому дёрнулись все шесть ручек сразу».
export const useКассу = (путь: string, enabled = true) =>
  useQuery({
    queryKey: ['money', 'cash', путь],
    queryFn: () => запрос<Агрегат>(`/cash${путь}`),
    enabled,
    ...общее,
  })

export const useФОТ = (путь: string, enabled = true) =>
  useQuery({
    queryKey: ['money', 'fot', путь],
    queryFn: () => запрос<Агрегат>(`/fot${путь}`),
    enabled,
    ...общее,
  })

export const useФОТПоЛюдям = (путь: string) =>
  useQuery({
    queryKey: ['money', 'fot-people', путь],
    queryFn: () => запрос<ФОТПоЛюдям>(`/fot-people${путь}`),
    ...общее,
  })

export const useОстатки = (путь: string) =>
  useQuery({ queryKey: ['money', 'balances', путь], queryFn: () => запрос<Остатки>(`/balances${путь}`), ...общее })

export const useДвижение = (путь: string) =>
  useQuery({ queryKey: ['money', 'ops', путь], queryFn: () => запрос<Движение>(`/ops${путь}`), ...общее })
